import { Empty, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { CustomViewWeekCalendarBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    CustomViewConfig,
    FilterCommonCondition,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewControlPaginateToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import { FIRST_DAY_OF_WEEK, VariableType } from '@lighthouse/core'
import type { ViewAppendParams } from '@lighthouse/shared'
import {
    findRecordInPool,
    getCursorStyle,
    getPaddingStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    scrollToRecord,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useLanguageContext,
    useRegisterViewControlListeners,
    ViewBlockTool,
    WEEK_CALENDAR_PAGE_SIZE
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { addDays, addWeeks, endOfDay, startOfDay, startOfWeek } from 'date-fns'
import { useStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { fetchViewRecordsAtom } from '@/atoms/blockRecordsDict/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import * as srv from '@/services'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useGroup } from '../hooks/useGroup'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useQuickFilter } from '../hooks/useQuickFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useSort } from '../hooks/useSort'
import { useSubscribeView } from '../hooks/useSubscribeView'
import { useTableProps } from '../hooks/useTableProps'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { CustomControllerProps } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const CustomWeekCalendarController: React.FC<CustomControllerProps> = ({ blockData, node, firstRef, onReady, children, ...rest }) => {
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        linkFilterController,
        quickFilter,
        groupConfigure,
        canGroup,
        canSearch,
        canSort,
        sorts,
        triggerWorkflow,
        importMode,
        repeat,
        compareFields,
        breakPoint: baseBreakpoint,
        toolbar,
        recordOperator,
        eventList,
        filter,
        calendarStartDateField,
        firstDayOfWeek,
        cursor
    } = config as CustomViewConfig

    const envId = useCurrentEnvId()

    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, record?.id, scope])

    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)
    // 快捷筛选器hook
    const { quickFiltersRule, handleQuickFilter } = useQuickFilter({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        quickFilter
    })

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    // 快捷筛选器hook
    const { tablePropsCache, handleDisplayChange } = useTableProps({
        appId,
        envId,
        blockId: uniqueId
    })

    // 分组hook
    const { initGroup, tabValue, tabList, handleTabChange, getFilterByTab, handleGroupChange, handleGetGroupRecordCount } = useGroup({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        canGroup,
        groupConfigure
    })

    // 排序hook
    const { mergeSort, handleSorterChange } = useSort({
        appId,
        envId,
        blockId: uniqueId,
        canSort,
        sorts
    })

    const [customViewCalendarDate, setCustomViewCalendarDate] = useState(() => {
        if (firstDayOfWeek === FIRST_DAY_OF_WEEK.MONDAY) {
            return startOfWeek(Date.now(), { weekStartsOn: 1 })
        }
        if (firstDayOfWeek === FIRST_DAY_OF_WEEK.SUNDAY) {
            return startOfWeek(Date.now(), { weekStartsOn: 0 })
        }
        return startOfDay(Date.now())
    })

    useUpdateEffect(() => {
        setCustomViewCalendarDate(() => {
            if (firstDayOfWeek === FIRST_DAY_OF_WEEK.MONDAY) {
                return startOfWeek(Date.now(), { weekStartsOn: 1 })
            }
            if (firstDayOfWeek === FIRST_DAY_OF_WEEK.SUNDAY) {
                return startOfWeek(Date.now(), { weekStartsOn: 0 })
            }
            return startOfDay(Date.now())
        })
    }, [firstDayOfWeek])

    const getFilterByCalendarDate = useCallback(() => {
        if (!calendarStartDateField) {
            return
        }

        const start = startOfDay(customViewCalendarDate)
        const end = endOfDay(addDays(start, 6))
        return [
            {
                idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: calendarStartDateField } },
                operator: '>=',
                paramList: [{ type: VariableType.VALUE, valueVariable: { value: start.valueOf(), type: 'date' } }]
            },
            {
                idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: calendarStartDateField } },
                operator: '<=',
                paramList: [{ type: VariableType.VALUE, valueVariable: { value: end.valueOf(), type: 'date' } }]
            }
        ] as FilterCommonCondition[]
    }, [calendarStartDateField, customViewCalendarDate])

    // 视图独立数据hook
    const {
        searchVal,
        currentPage = 1,
        rowTotal,
        recordPoints,
        handleSearch
    } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })

    const viewIndependentDataOmitRecord = useMemo(
        () => ({
            dsId: pointer,
            currentPage,
            rowTotal,
            search: searchVal
        }),
        [currentPage, pointer, rowTotal, searchVal]
    )

    const { convertTextByLanguage } = useLanguageContext()

    const allRichtext = useMemo(() => {
        const toolbarRich = toolbar?.map(item => item.name) || []
        const recordOperatorRich = recordOperator?.map(item => item.name) || []

        return [...toolbarRich, ...recordOperatorRich, baseBreakpoint.emptyView?.title].filter(Boolean) as RichTextContentProtocol[]
    }, [toolbar, recordOperator, baseBreakpoint.emptyView?.title])
    const handleRenderRichText = useRenderRichText(allRichtext)

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    // 首批数据加载完成且渲染完成后调用 onReady
    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            // 双重 requestAnimationFrame 确保 DOM 渲染完成
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { ref, width: blockWidth, breakPoint } = useBreakpoint([!!fetchViewRecordsValue])

    // 使用新的依赖管理方案，优化重新渲染（仅包含对象/数组类型字段）
    const stableDeps = useFetchViewDataDeps({
        quickFiltersRule,
        resolvedFilter,
        resolvedLinkFilter,
        sorts: mergeSort
    })

    const fetchViewData = useCallback(async () => {
        const noGroupOrHasTab = !initGroup.groupByFieldId || (initGroup.groupByFieldId && tabValue)
        const quickFiltersWidthWeek = getFilterByCalendarDate()
        if (!quickFiltersWidthWeek || !noGroupOrHasTab) {
            return
        }
        const quickFiltersWidthTab: FilterCommonCondition[] = [
            ...(stableDeps.quickFiltersRule || []),
            ...quickFiltersWidthWeek,
            ...getFilterByTab(tabValue)
        ]

        await mutation({
            stackId,
            pageId,
            viewId: id,
            uniqueId,
            scope,
            isSyncComponent: !!scope,
            pagination: { currentPage: 1, pageSize: WEEK_CALENDAR_PAGE_SIZE },
            params: {
                quickFilters: quickFiltersWidthTab,
                sorts: stableDeps.sorts,
                linkFilter: stableDeps.resolvedLinkFilter,
                filter: stableDeps.resolvedFilter
            },
            search: searchVal,
            disableFetchCount: true
        })
    }, [
        initGroup.groupByFieldId,
        tabValue,
        getFilterByCalendarDate,
        stableDeps.quickFiltersRule,
        stableDeps.sorts,
        stableDeps.resolvedLinkFilter,
        stableDeps.resolvedFilter,
        getFilterByTab,
        mutation,
        stackId,
        pageId,
        id,
        uniqueId,
        scope,
        searchVal
    ])

    const getGroupRecordCount = useCallback(
        () =>
            handleGetGroupRecordCount({
                quickFilters: stableDeps.quickFiltersRule,
                filter: stableDeps.resolvedFilter,
                linkFilter: stableDeps.resolvedLinkFilter,
                search: searchVal
            }),
        [handleGetGroupRecordCount, stableDeps.quickFiltersRule, stableDeps.resolvedFilter, stableDeps.resolvedLinkFilter, searchVal]
    )

    const handleAction = useCallback(
        (id: string, actorEvent: ActorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger, record]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    useConfigure({
        blockData,
        fetchViewData,
        getGroupRecordCount
    })

    // 接受其他命令执行加载数据操作
    useSubscribeView({ pointer, fetchViewData, pageNum: currentPage })

    const quickFilterData = useMemo(() => {
        return {
            mode: quickFilter?.mode || 'normal',
            rules: quickFiltersRule
        }
    }, [quickFilter?.mode, quickFiltersRule])

    const handleViewRefresh = useCallback(() => {
        fetchViewData()
    }, [fetchViewData])

    const store = useStore()

    // 扔到上层
    useRegisterViewControlListeners(id, scope, 'view', {
        refresh: handleViewRefresh,
        paginateTo: (payload: ViewControlPaginateToAction['params'] | undefined) => {
            const paginateToType = payload?.paginateToType

            if (!paginateToType) {
                return
            }

            const step = paginateToType === 'next' ? 1 : -1

            setCustomViewCalendarDate(s => addWeeks(s, step))
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    const handleAppend = useCallback(
        async (params: ViewAppendParams) => {
            const isAppend = await srv.appendDataToDataSource({
                ...params,
                sheetDto: {
                    ...params.sheetDto,
                    envId,
                    triggerWorkflow,
                    repeat,
                    compareFields,
                    importMode
                }
            })
            isAppend && Toast.success(convertTextByLanguage('importSuccess'))
            fetchViewData()
            return isAppend
        },
        [envId, triggerWorkflow, repeat, compareFields, importMode, convertTextByLanguage, fetchViewData]
    )

    return useMemo(() => {
        if (!pointer || !dataSource) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_datasource.svg')} alt="请在「右侧栏>配置」中选择数据来源" />}
                    description="请在「右侧栏>配置」中选择数据来源"
                />
            )
        }

        if (!calendarStartDateField) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_content.svg')} alt="请在「右侧栏>配置」中选择开始时间字段" />}
                    description="请在「右侧栏>配置」中选择开始时间字段"
                />
            )
        }

        // 首次加载未完成时（包括请求未开始和请求进行中），不渲染内容，避免闪现"暂无记录"
        if (firstRef.current && !fetchViewRecordsValue) {
            return null
        }
        // 首次加载完成，标记为非首次
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: baseBreakpoint.size.overflow,
                    ...rest.style,
                    ...(viewType === 'custom'
                        ? { ...getPaddingStyle(baseBreakpoint.viewLayout?.viewPadding, true), ...getCursorStyle(appId, cursor) }
                        : {})
                }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockTool
                            appId={appId}
                            envId={envId}
                            uniqueId={uniqueId}
                            dataSource={dataSource}
                            dataSourceList={dataSourceList}
                            tabList={tabList}
                            tabValue={tabValue}
                            blockData={blockData}
                            quickFilterData={quickFilterData}
                            viewIndependentDataOmitRecord={viewIndependentDataOmitRecord}
                            groupCache={initGroup}
                            tablePropsCache={tablePropsCache}
                            onTabChange={handleTabChange}
                            onSearch={handleSearch}
                            onFilter={handleQuickFilter}
                            onChangeDisplay={handleDisplayChange}
                            onChangeGroup={handleGroupChange}
                            onChangeSorter={handleSorterChange}
                            onAppend={handleAppend}
                            onToolbarActionTrigger={handleAction}
                            onRenderButtonTitle={handleRenderRichText}
                        >
                            <ViewBlockProvider
                                uniqueId={uniqueId}
                                blockData={blockData}
                                dataSource={dataSource}
                                recordPoints={recordPoints}
                                recordPoolAtom={recordPoolAtom}
                                dataSourceList={dataSourceList}
                            >
                                <CustomViewWeekCalendarBlock
                                    firstDayOfWeek={customViewCalendarDate}
                                    node={node}
                                    blockWidth={blockWidth}
                                    breakPoint={breakPoint}
                                    onRenderRichText={handleRenderRichText}
                                    onWeekChange={setCustomViewCalendarDate}
                                    onRecordClick={handleRecordClickEvent}
                                />
                            </ViewBlockProvider>
                        </ViewBlockTool>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        calendarStartDateField,
        firstRef,
        fetchViewRecordsValue,
        id,
        title,
        viewType,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        baseBreakpoint.viewLayout?.viewPadding,
        rest,
        appId,
        cursor,
        ref,
        breakPoint,
        envId,
        uniqueId,
        dataSourceList,
        tabList,
        tabValue,
        blockData,
        quickFilterData,
        viewIndependentDataOmitRecord,
        initGroup,
        tablePropsCache,
        handleTabChange,
        handleSearch,
        handleQuickFilter,
        handleDisplayChange,
        handleGroupChange,
        handleSorterChange,
        handleAppend,
        handleAction,
        handleRenderRichText,
        recordPoints,
        customViewCalendarDate,
        node,
        blockWidth,
        handleRecordClickEvent,
        children
    ])
}

export default CustomWeekCalendarController
